import React, { PropsWithChildren } from 'react';
import {
  ProductHourAndDate,
  Product,
  ProductName,
  Price,
  PackageTotalPrice,
  PackagePriceInfo,
  AdditionalWrapper,
  ProductProvider,
} from './styles';

import { formatCents } from '@src/common';

import { dateBySellingMode, getTariffTotalPrice } from '@src/common/checkout';
import { SaleItem as Item } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import { appInfoSelector } from '@src/features/auth';
import { useAppSelector } from '@src/core/hooks';
import ErrorWarning from '../Cart/CartDetails/ErrorWarning';

interface Props {
  item: Item;
}

const SaleItem = (props: PropsWithChildren<Props>) => {
  const { item, children } = props;

  const { i18n } = useTranslate();
  const appInfo = useAppSelector(appInfoSelector);

  return (
    <Product key={item.id}>
      <ProductHourAndDate>{dateBySellingMode({ item, i18n })}</ProductHourAndDate>

      <ProductName>
        {item.product.name} ({item.quantity})
      </ProductName>

      {item.product.appId !== appInfo?.id ? (
        <ProductProvider>{i18n('product_package_adder_sold_by', { owner: item.product.app.name })}</ProductProvider>
      ) : null}

      <Price>{formatCents(getTariffTotalPrice(item.tariffs))}</Price>
      {item.resources
        ? item?.resources.map((saleResource) => (
            <AdditionalWrapper key={saleResource.resource.name}>
              <ProductName>{saleResource.resource.formattedName}</ProductName>

              {saleResource?.priceCents ? <Price>{formatCents(saleResource?.priceCents)}</Price> : null}
            </AdditionalWrapper>
          ))
        : null}

      {item.additionals
        ? item.additionals.map((saleAdditional) => (
            <AdditionalWrapper key={saleAdditional.id}>
              <ProductName>
                {saleAdditional.additional.title} ({saleAdditional.unitQuantity})
              </ProductName>

              <Price>{formatCents(saleAdditional.priceCents)}</Price>
            </AdditionalWrapper>
          ))
        : null}

      {children}

      {Number.isFinite(item.productPackageOriginalDiscount) ? (
        <ErrorWarning variant='warning'>{i18n('discount_fallback_warning_message')}</ErrorWarning>
      ) : null}
    </Product>
  );
};

export const SaleItemWithPrice = ({ item }: { item: Item }) => {
  const { i18n } = useTranslate();

  return (
    <SaleItem item={item}>
      <PackagePriceInfo>
        {i18n('cart_package_total')}
        <PackageTotalPrice>{formatCents(item.totalPriceCents)}</PackageTotalPrice>
      </PackagePriceInfo>
    </SaleItem>
  );
};

export default SaleItem;
